// Layered Reveal — React port of kula-create's layered-reveal.html.
//
// Pedagogy: progressive reframe. Each layer adds context that shifts the
// learner's interpretation of what came before. Per-layer assessment prompts
// capture the evolving view; a final synthesis reveals the full picture.
//
// Per-layer response: { id, assessment: { [promptId]: value } }.

import { useState, useCallback, useEffect, useRef } from 'react';
import { AnimatePresence } from 'framer-motion';
import type { LayeredRevealSlideData, Theme, ExperienceConclusion } from '../../slideTypes';
import { slideTypographyStyle } from '../../slideTypes';
import { Reveal } from '../../shared/Reveal';
import { ExperienceClose } from '../../shared/ExperienceClose';
import type { ActivityResponsePayload } from '../../../../../types';

interface Props {
  data: LayeredRevealSlideData;
  theme: Theme;
  isEditing: boolean;
  onUpdate: (data: LayeredRevealSlideData) => void;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  onComplete?: () => void;
  savedResponse?: Record<string, unknown> | null;
}

export function LayeredRevealSlide({
  data,
  theme,
  isEditing,
  onUpdate,
  onActivityResponse,
  onComplete,
  savedResponse,
}: Props) {
  if (isEditing) return <BasicEditor data={data} theme={theme} onUpdate={onUpdate} />;
  return (
    <Student
      data={data}
      theme={theme}
      onActivityResponse={onActivityResponse}
      onComplete={onComplete}
      savedResponse={savedResponse}
    />
  );
}

function Student({
  data,
  theme,
  onActivityResponse,
  onComplete,
  savedResponse,
}: {
  data: LayeredRevealSlideData;
  theme: Theme;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  onComplete?: () => void;
  savedResponse?: Record<string, unknown> | null;
}) {
  const accent = theme.colors.accent || theme.colors.primary;

  // Saved payload is per-layer (last only) — only the FINAL layer's id proves the
  // whole flow finished; an earlier layer's payload can't reconstruct what followed, so start fresh.
  const lastLayer = data.layers[data.layers.length - 1];
  const restored = !!(lastLayer && savedResponse && savedResponse.id === lastLayer.id);
  const savedAssessment =
    restored && savedResponse?.assessment && typeof savedResponse.assessment === 'object'
      ? (savedResponse.assessment as Record<string, string>)
      : null;

  const [currentLayerIdx, setCurrentLayerIdx] = useState(restored ? data.layers.length - 1 : 0);
  const [assessments, setAssessments] = useState<Record<string, Record<string, string>>>(() =>
    savedAssessment ? { [lastLayer!.id]: savedAssessment } : {},
  );
  const [completedLayers, setCompletedLayers] = useState<Set<string>>(() =>
    restored ? new Set(data.layers.map((l) => l.id)) : new Set(),
  );
  const completedRef = useRef(restored);

  const allDone = completedLayers.size === data.layers.length && data.layers.length > 0;

  const updateAssessment = (layerId: string, promptId: string, value: string) =>
    setAssessments((prev) => ({
      ...prev,
      [layerId]: { ...(prev[layerId] ?? {}), [promptId]: value },
    }));

  // Scale sliders display a default of 3 before the learner touches them — seed the
  // recorded state when a layer becomes active so display and state never diverge.
  useEffect(() => {
    const layer = data.layers[currentLayerIdx];
    if (!layer || completedLayers.has(layer.id)) return;
    setAssessments((prev) => {
      const existing = prev[layer.id] ?? {};
      let changed = false;
      const seeded = { ...existing };
      for (const p of layer.assessmentPrompts) {
        if (p.inputType === 'scale' && (seeded[p.id] ?? '').trim().length === 0) {
          seeded[p.id] = '3';
          changed = true;
        }
      }
      return changed ? { ...prev, [layer.id]: seeded } : prev;
    });
  }, [currentLayerIdx, data.layers, completedLayers]);

  const isLayerReady = (layer: LayeredRevealSlideData['layers'][number]) => {
    const ans = assessments[layer.id] ?? {};
    return layer.assessmentPrompts.every((p) => (ans[p.id] ?? '').trim().length > 0);
  };

  const completeLayer = useCallback(() => {
    const layer = data.layers[currentLayerIdx];
    if (!layer) return;
    onActivityResponse?.({
      activityType: 'layered-reveal',
      mode: 'survey',
      responseData: { id: layer.id, assessment: assessments[layer.id] ?? {} },
    });
    setCompletedLayers((prev) => new Set(prev).add(layer.id));
    if (currentLayerIdx < data.layers.length - 1) {
      setCurrentLayerIdx((i) => i + 1);
    } else if (!completedRef.current) {
      completedRef.current = true;
      onComplete?.();
    }
  }, [data.layers, currentLayerIdx, assessments, onActivityResponse, onComplete]);

  // Close reflect-back: the assessment trail — first layer's rating vs the last,
  // making the reappraisal tangible (review §15). Reads the first scale prompt in
  // each layer; absent ratings degrade to the no-number default.
  const scaleValue = (layer: LayeredRevealSlideData['layers'][number] | undefined) => {
    const sp = layer?.assessmentPrompts.find((p) => p.inputType === 'scale');
    const v = sp ? Number(assessments[layer!.id]?.[sp.id]) : NaN;
    return Number.isNaN(v) ? undefined : v;
  };
  const firstRating = scaleValue(data.layers[0]);
  const finalRating = scaleValue(data.layers[data.layers.length - 1]);
  const closeValues = {
    firstRating,
    finalRating,
    shift: firstRating !== undefined && finalRating !== undefined ? finalRating - firstRating : undefined,
  };
  const conclusion: ExperienceConclusion = data.conclusion ?? {
    heading: 'So what',
    body:
      firstRating !== undefined && finalRating !== undefined
        ? 'Your read moved from {firstRating} to {finalRating} as the layers came in.'
        : 'Each layer reframed the one before it.',
    reflectionPrompt: data.synthesis.reflectionPrompt,
  };

  return (
    <div
      className="h-full w-full overflow-y-auto"
      style={{ background: theme.colors.background, color: theme.colors.text, fontFamily: theme.fonts.body }}
    >
      <div className="mx-auto max-w-[760px] px-6 py-10">
        <header className="text-center mb-6">
          <h1 style={{ ...slideTypographyStyle(theme, 'heading'), color: theme.colors.text, marginBottom: 8 }}>
            {data.title}
          </h1>
          {data.subtitle && <p style={{ color: theme.colors.textMuted, fontSize: '0.95rem' }}>{data.subtitle}</p>}
        </header>

        <div className="rounded-lg p-5 mb-6 border-l-4" style={{ background: theme.colors.surface, borderColor: accent }}>
          <p className="text-xs uppercase tracking-wider font-bold mb-2" style={{ color: accent }}>Scenario</p>
          <p className="leading-relaxed text-sm" style={{ color: theme.colors.text }}>{data.scenario.context}</p>
        </div>

        {/* Empty state — registry default is layers: [] */}
        {data.layers.length === 0 && (
          <div
            className="rounded-lg border p-6 text-center"
            style={{ background: theme.colors.surface, borderColor: `${accent}30` }}
          >
            <p className="text-sm" style={{ color: theme.colors.textMuted }}>
              This activity has no layers yet — there is nothing to reveal here.
            </p>
          </div>
        )}

        {/* Layer stack */}
        {data.layers.map((layer, idx) => {
          if (idx > currentLayerIdx) return null;
          const isDone = completedLayers.has(layer.id);
          const isCurrent = idx === currentLayerIdx && !allDone;

          return (
            <Reveal
              key={layer.id}
              variant="up"
              className="rounded-lg p-5 mb-4 border-2"
              style={{
                background: theme.colors.surface,
                borderColor: isCurrent ? accent : `${accent}30`,
                opacity: !isCurrent && isDone ? 0.7 : 1,
              }}
            >
              <div className="flex items-center gap-2 mb-3">
                <span
                  className="inline-flex items-center justify-center w-7 h-7 rounded-full font-bold text-sm"
                  style={{ background: accent, color: '#fff' }}
                >
                  {idx + 1}
                </span>
                <h3 className="font-semibold" style={{ color: theme.colors.text }}>{layer.label}</h3>
              </div>
              <ContentBlock contentType={layer.contentType} content={layer.content} theme={theme} accent={accent} />

              <div className="mt-4 space-y-3">
                {layer.assessmentPrompts.map((p) => (
                  <div key={p.id}>
                    <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.text }}>{p.text}</label>
                    {p.inputType === 'select' ? (
                      <select
                        value={assessments[layer.id]?.[p.id] ?? ''}
                        disabled={isDone}
                        onChange={(e) => updateAssessment(layer.id, p.id, e.target.value)}
                        className="w-full px-3 py-2 rounded border text-sm"
                        style={{
                          background: theme.colors.background,
                          borderColor: `${theme.colors.textMuted}33`,
                          color: theme.colors.text,
                        }}
                      >
                        <option value="">Select…</option>
                        {(p.options ?? []).map((opt, optIdx) => <option key={optIdx} value={opt}>{opt}</option>)}
                      </select>
                    ) : p.inputType === 'scale' ? (
                      <ScaleInput
                        value={Number(assessments[layer.id]?.[p.id]) || 3}
                        labels={p.scaleLabels ?? ['Low', 'High']}
                        onChange={(v) => updateAssessment(layer.id, p.id, String(v))}
                        accent={accent}
                        theme={theme}
                        disabled={isDone}
                      />
                    ) : (
                      <textarea
                        value={assessments[layer.id]?.[p.id] ?? ''}
                        disabled={isDone}
                        onChange={(e) => updateAssessment(layer.id, p.id, e.target.value)}
                        maxLength={p.maxLength ?? 200}
                        rows={2}
                        className="w-full px-3 py-2 rounded border text-sm"
                        style={{
                          background: theme.colors.background,
                          borderColor: `${theme.colors.textMuted}33`,
                          color: theme.colors.text,
                        }}
                      />
                    )}
                  </div>
                ))}
              </div>

              {isCurrent && (
                <button
                  onClick={completeLayer}
                  disabled={!isLayerReady(layer)}
                  className="mt-4 w-full px-6 py-3 rounded-lg font-semibold text-sm"
                  style={{
                    background: accent,
                    color: '#fff',
                    cursor: isLayerReady(layer) ? 'pointer' : 'not-allowed',
                    opacity: isLayerReady(layer) ? 1 : 0.4,
                  }}
                >
                  {idx < data.layers.length - 1 ? 'Reveal next layer' : 'Complete'}
                </button>
              )}
            </Reveal>
          );
        })}

        {/* Synthesis reveal — the payoff. Heavier accent rule + larger radius. */}
        <AnimatePresence>
          {allDone && (
            <Reveal
              variant="scale"
              spring="bouncy"
              className="rounded-2xl p-5 border-l-[5px] shadow-sm mt-4"
              style={{ background: theme.colors.surface, borderColor: accent }}
            >
              <p className="text-xs uppercase tracking-wider font-bold mb-2" style={{ color: accent }}>
                The full picture
              </p>
              <p className="leading-relaxed text-sm" style={{ color: theme.colors.text }}>
                {data.synthesis.revealText}
              </p>
            </Reveal>
          )}
        </AnimatePresence>

        {/* Authored "So what?" close — shows the assessment trail (first → final
            rating) and turns the old dead-text reflection prompt into an actual
            input. Sits below the synthesis reveal. */}
        <AnimatePresence>
          {allDone && (
            <Reveal variant="up" className="mt-4">
              <ExperienceClose
                conclusion={conclusion}
                theme={theme}
                values={closeValues}
                fallback="Look back at how your assessment changed as each layer was revealed."
              />
            </Reveal>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

function ContentBlock({
  contentType,
  content,
  theme,
  accent,
}: {
  contentType: LayeredRevealSlideData['layers'][number]['contentType'];
  content: string;
  theme: Theme;
  accent: string;
}) {
  if (contentType === 'quote') {
    return (
      <blockquote className="border-l-4 pl-4 py-1" style={{ borderColor: accent }}>
        <p className="italic" style={{ color: theme.colors.text }}>“{content}”</p>
      </blockquote>
    );
  }
  if (contentType === 'data') {
    return (
      <div className="rounded p-3" style={{ background: `${accent}10`, border: `1px solid ${accent}30` }}>
        <p className="text-sm" style={{ color: theme.colors.text }}>{content}</p>
      </div>
    );
  }
  if (contentType === 'image-description') {
    return (
      <div className="border-l-4 pl-4 py-1" style={{ borderColor: accent, background: `${accent}08` }}>
        <p className="italic text-sm" style={{ color: theme.colors.textMuted }}>{content}</p>
      </div>
    );
  }
  return <p className="leading-relaxed text-sm" style={{ color: theme.colors.text }}>{content}</p>;
}

function ScaleInput({
  value,
  labels,
  onChange,
  accent,
  theme,
  disabled,
}: {
  value: number;
  labels: [string, string];
  onChange: (v: number) => void;
  accent: string;
  theme: Theme;
  disabled?: boolean;
}) {
  const pct = ((value - 1) / 4) * 100;
  return (
    <div>
      <div className="flex justify-between text-xs mb-1" style={{ color: theme.colors.textMuted }}>
        <span>{labels[0]}</span>
        <span>{labels[1]}</span>
      </div>
      <input
        type="range"
        min={1}
        max={5}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full h-2 rounded-lg appearance-none cursor-pointer"
        style={{
          background: `linear-gradient(to right, ${accent} 0%, ${accent} ${pct}%, ${theme.colors.textMuted}33 ${pct}%, ${theme.colors.textMuted}33 100%)`,
        }}
      />
    </div>
  );
}

function BasicEditor({
  data,
  theme,
  onUpdate,
}: {
  data: LayeredRevealSlideData;
  theme: Theme;
  onUpdate: (data: LayeredRevealSlideData) => void;
}) {
  return (
    <div className="h-full w-full overflow-y-auto p-8">
      <div className="mx-auto max-w-[720px] space-y-3">
        <input
          value={data.title}
          onChange={(e) => onUpdate({ ...data, title: e.target.value })}
          placeholder="Title…"
          className="w-full bg-transparent border-b-2 border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none"
          style={{ ...slideTypographyStyle(theme, 'heading'), color: theme.colors.text }}
        />
        <input
          value={data.subtitle}
          onChange={(e) => onUpdate({ ...data, subtitle: e.target.value })}
          placeholder="Subtitle"
          className="w-full bg-transparent border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none text-sm"
          style={{ color: theme.colors.textMuted }}
        />
        <textarea
          value={data.scenario.context}
          onChange={(e) => onUpdate({ ...data, scenario: { context: e.target.value } })}
          placeholder="Scenario context"
          rows={4}
          className="w-full px-3 py-2 rounded border text-sm"
          style={{
            background: theme.colors.surface,
            borderColor: `${theme.colors.textMuted}33`,
            color: theme.colors.text,
          }}
        />
        <textarea
          value={data.synthesis?.revealText ?? ''}
          onChange={(e) => onUpdate({ ...data, synthesis: { ...data.synthesis, revealText: e.target.value } })}
          placeholder="Synthesis reveal text"
          rows={3}
          className="w-full px-3 py-2 rounded border text-sm"
          style={{
            background: theme.colors.surface,
            borderColor: `${theme.colors.textMuted}33`,
            color: theme.colors.text,
          }}
        />
        <div className="rounded p-3 text-xs" style={{ background: `${theme.colors.textMuted}11`, color: theme.colors.textMuted }}>
          {data.layers.length} layer{data.layers.length === 1 ? '' : 's'}.
          Structural edits AI-only — regenerate via AI build.
        </div>
      </div>
    </div>
  );
}
